import apiClient, { ApiError } from './client';
import { API_BASE_URL, API_ENDPOINTS, STORAGE_KEYS } from './config';
import type {
	ProviderProfile,
	ProviderDashboardData,
	ProviderLocation,
	ProviderService,
	ServiceRequest,
	BulkUploadResponse,
} from '../types/provider';
import type { PaymentSetupResponse, PaymentVerifyRequest } from '../types/payment';
import type { ProviderTcrParams, ProviderTcrResponse } from '../types/providerTcr';

// Request type for adding a new location
export interface AddLocationRequest {
	locationName: string;
	address: string;
	city: string;
	state: string;
	zipCode: string;
	phone: string;
	email: string;
	active: boolean;
	primary: boolean;
}

// Provider Profile API methods (for logged-in SALON_OWNER)
export const providerProfileApi = {
	/**
	 * Get the current provider's profile data
	 * Includes locations, subscription status, and setup flags
	 */
	getProfile: async (): Promise<ProviderProfile> => {
		return apiClient.get<ProviderProfile>(API_ENDPOINTS.PROVIDER.ME);
	},

	/**
	 * Get dashboard data for a specific location
	 * Includes today's revenue, appointments, stylists, and customers
	 */
	getDashboard: async (locationId: number): Promise<ProviderDashboardData> => {
		return apiClient.get<ProviderDashboardData>(
			`${API_ENDPOINTS.PROVIDER.DASHBOARD}?locationId=${locationId}`
		);
	},

	/**
	 * Add a new location for the current provider
	 */
	addLocation: async (data: AddLocationRequest): Promise<ProviderLocation> => {
		return apiClient.post<ProviderLocation>(API_ENDPOINTS.LOCATION, data);
	},

	/**
	 * Update an existing location
	 */
	updateLocation: async (
		locationId: number,
		data: AddLocationRequest
	): Promise<ProviderLocation> => {
		return apiClient.put<ProviderLocation>(`${API_ENDPOINTS.LOCATION}/${locationId}`, data);
	},

	/**
	 * Delete a location by ID
	 */
	deleteLocation: async (locationId: number): Promise<void> => {
		await apiClient.delete(`${API_ENDPOINTS.LOCATION}/${locationId}`);
	},

	/**
	 * Get all services for a specific location
	 */
	getServices: async (locationId: number): Promise<ProviderService[]> => {
		return apiClient.get<ProviderService[]>(
			`${API_ENDPOINTS.SERVICE.MY_SERVICES}?locationId=${locationId}`
		);
	},

	/**
	 * Create a new service for a specific location
	 */
	createService: async (locationId: number, data: ServiceRequest): Promise<ProviderService> => {
		return apiClient.post<ProviderService>(
			`${API_ENDPOINTS.SERVICE.BASE}?locationId=${locationId}`,
			data
		);
	},

	/**
	 * Update an existing service
	 */
	updateService: async (
		serviceId: number,
		locationId: number,
		data: ServiceRequest
	): Promise<ProviderService> => {
		return apiClient.put<ProviderService>(
			`${API_ENDPOINTS.SERVICE.BASE}/${serviceId}?locationId=${locationId}`,
			data
		);
	},

	/**
	 * Delete a service by ID
	 */
	deleteService: async (serviceId: number, locationId: number): Promise<void> => {
		await apiClient.delete(
			`${API_ENDPOINTS.SERVICE.BASE}/${serviceId}?locationId=${locationId}`
		);
	},

	/**
	 * Initiate payment bank account setup via Stripe
	 * Returns client secret and Stripe publishable key for client-side flow
	 */
	setupPayment: async (providerId: number): Promise<PaymentSetupResponse> => {
		return apiClient.post<PaymentSetupResponse>(
			API_ENDPOINTS.PROVIDER.PAYMENT_SETUP(providerId),
			{}
		);
	},

	/**
	 * Confirm bank account setup with the backend after Stripe confirmation
	 */
	confirmPayment: async (providerId: number): Promise<void> => {
		await apiClient.post(API_ENDPOINTS.PROVIDER.PAYMENT_CONFIRM(providerId), {});
	},

	/**
	 * Verify bank account micro-deposits via the backend
	 */
	verifyPayment: async (providerId: number, data: PaymentVerifyRequest): Promise<void> => {
		await apiClient.post(API_ENDPOINTS.PROVIDER.PAYMENT_VERIFY(providerId), data);
	},

	/**
	 * Get TCR approvals list for a location with optional filters
	 */
	getTcrApprovals: async (params: ProviderTcrParams): Promise<ProviderTcrResponse> => {
		const queryParams = new URLSearchParams();
		queryParams.append('locationId', String(params.locationId));
		if (params.status && params.status !== 'ALL') queryParams.append('status', params.status);
		if (params.search) queryParams.append('search', params.search);
		if (params.staffId) queryParams.append('staffId', params.staffId);
		return apiClient.get<ProviderTcrResponse>(
			`${API_ENDPOINTS.PROVIDER.TCR_APPROVALS}?${queryParams.toString()}`
		);
	},

	/**
	 * Approve a TCR by ID
	 */
	approveTcr: async (id: number): Promise<void> => {
		await apiClient.patch(API_ENDPOINTS.PROVIDER.TCR_APPROVE(id));
	},

	/**
	 * Reject a TCR by ID with review notes
	 */
	rejectTcr: async (id: number, reviewNotes: string): Promise<void> => {
		await apiClient.patch(API_ENDPOINTS.PROVIDER.TCR_REJECT(id), { reviewNotes });
	},

	/**
	 * Bulk upload services via CSV (multipart/form-data)
	 * Uses raw fetch since apiClient always sets Content-Type: application/json
	 */
	bulkUploadServices: async (locationId: number, file: File): Promise<BulkUploadResponse> => {
		const formData = new FormData();
		formData.append('file', file);

		const token =
			localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
			sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

		const response = await fetch(
			`${API_BASE_URL}${API_ENDPOINTS.SERVICE.BULK_UPLOAD}?locationId=${locationId}`,
			{
				method: 'POST',
				headers: token ? { Authorization: `Bearer ${token}` } : {},
				body: formData,
			}
		);

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new ApiError(
				errorData?.message || 'Bulk upload failed',
				response.status,
				errorData?.fieldErrors
			);
		}

		return response.json();
	},

	/**
	 * Get the status of a bulk upload job
	 */
	getBulkUploadStatus: async (jobId: number): Promise<BulkUploadResponse> => {
		return apiClient.get<BulkUploadResponse>(API_ENDPOINTS.SERVICE.BULK_UPLOAD_STATUS(jobId));
	},

	/**
	 * Download CSV template for bulk upload (returns CSV string)
	 */
	downloadServiceTemplate: async (locationId: number): Promise<string> => {
		const result = await apiClient.get<string>(
			`${API_ENDPOINTS.SERVICE.BULK_UPLOAD_TEMPLATE}?locationId=${locationId}`
		);
		return result;
	},
};

export default providerProfileApi;
